import React, { useEffect, useMemo, useState } from 'react';
import CommercialSetupWizard from './CommercialSetupWizard';
import CommercialDashboardTab from './CommercialDashboardTab';
import {
  CommercialAssetsTab,
  CommercialCustomersTab,
  CommercialScheduleTab,
  CommercialTechniciansTab,
} from './CommercialResourceTabs';
import {
  CommercialDispatchTab,
  CommercialReportsTab,
  CommercialWorkOrdersTab,
} from './CommercialWorkflowTabs';
import CommercialWorkforceTab from './CommercialWorkforceTab';
import CommercialAdminTab from './CommercialAdminTab';
import { TenantOverviewPanel } from './CommercialCommon';
import './commercial.css';

const navigationItems = [
  { key: 'setup', title: 'Setup', description: 'Create your company workspace', icon: 'S' },
  { key: 'dashboard', title: 'Dashboard', description: 'Live activity and key numbers', icon: 'D' },
  { key: 'work-orders', title: 'Work Orders', description: 'Create and manage work', icon: 'W' },
  { key: 'schedule', title: 'Schedule', description: 'Locations, teams, and shifts', icon: 'C' },
  { key: 'dispatch', title: 'Dispatch', description: 'Track work in motion', icon: 'P' },
  { key: 'coverage', title: 'Coverage', description: 'Weekly staffing and time off', icon: 'V' },
  { key: 'assets', title: 'Assets', description: 'Equipment and service records', icon: 'A' },
  { key: 'customers', title: 'Customers', description: 'Contacts and customer accounts', icon: 'U' },
  { key: 'technicians', title: 'Technicians', description: 'People, skills, and availability', icon: 'T' },
  { key: 'reports', title: 'Reports', description: 'Performance and workload insights', icon: 'R' },
  { key: 'admin', title: 'Admin', description: 'Users, access, and company settings', icon: 'M' },
];

const emptyWorkspace = {
  tenant: null,
  summary: null,
  locations: [],
  teams: [],
  shifts: [],
  technicians: [],
  customers: [],
  assets: [],
  workOrders: [],
  recentInvitations: [],
  recentAuditLogs: [],
  workOrderStatuses: ['unassigned', 'scheduled', 'traveling', 'in_progress', 'paused', 'completed', 'closed'],
  workOrderPriorities: ['low', 'medium', 'high', 'urgent'],
  capabilities: {},
};

export default function CommercialApp({ api, user, handleLogout }) {
  const [tenantContext, setTenantContext] = useState({
    tenant: null,
    canManageInvites: false,
    canManageUsers: false,
    currentUserRole: user?.role || '',
    capabilities: {},
  });
  const [workspace, setWorkspace] = useState(emptyWorkspace);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState('');
  const [activeSection, setActiveSection] = useState(user?.commercialTenantId ? 'dashboard' : 'setup');
  const hasTenant = Boolean(tenantContext.tenant || workspace.tenant || user?.commercialTenantId);

  useEffect(() => {
    let isMounted = true;

    const loadTenant = async () => {
      if (!api || !user?.email) return;

      try {
        const res = await api.get('/commercial/tenant/current', {
          params: { email: user.email },
        });
        if (!isMounted) return;
        setTenantContext({
          tenant: res.data.tenant || null,
          canManageInvites: Boolean(res.data.canManageInvites),
          canManageUsers: Boolean(res.data.canManageUsers),
          currentUserRole: res.data.currentUserRole || user?.role || '',
          capabilities: res.data.capabilities || {},
        });
      } catch (err) {
        if (!isMounted) return;
        setTenantContext({
          tenant: null,
          canManageInvites: false,
          canManageUsers: false,
          currentUserRole: user?.role || '',
          capabilities: {},
        });
      }
    };

    loadTenant();
    return () => {
      isMounted = false;
    };
  }, [api, user]);

  const loadWorkspace = async () => {
    if (!api || !user?.email || !(user?.commercialTenantId || tenantContext.tenant?.id)) return;

    setWorkspaceLoading(true);
    setWorkspaceError('');
    try {
      const res = await api.get('/commercial/workspace/bootstrap', {
        params: { requesterEmail: user.email },
      });
      setWorkspace(res.data);
    } catch (err) {
      setWorkspaceError(err.response?.data?.message || 'Unable to load the commercial workspace.');
    } finally {
      setWorkspaceLoading(false);
    }
  };

  useEffect(() => {
    if (user?.commercialTenantId || tenantContext.tenant?.id) {
      loadWorkspace();
    }
  }, [user?.commercialTenantId, tenantContext.tenant?.id]);

  useEffect(() => {
    if (hasTenant && activeSection === 'setup') {
      setActiveSection('dashboard');
    }
  }, [hasTenant, activeSection]);

  const visibleNavigationItems = useMemo(
    () => navigationItems.filter(item => item.key !== 'setup' || !hasTenant),
    [hasTenant]
  );

  const activeNav = useMemo(
    () => visibleNavigationItems.find(item => item.key === activeSection) || visibleNavigationItems[0] || navigationItems[0],
    [activeSection, visibleNavigationItems]
  );

  const hasWorkspace = Boolean(workspace.tenant);

  const renderActiveSection = () => {
    if (activeSection === 'setup') {
      return (
        <div className="commercial-grid">
          {tenantContext.tenant ? (
            <TenantOverviewPanel tenant={workspace.tenant || tenantContext.tenant} summary={workspace.summary} />
          ) : (
            <CommercialSetupWizard
              api={api}
              creatorEmail={user?.email}
              onTenantCreated={data => {
                setTenantContext({
                  tenant: data.tenant,
                  canManageInvites: true,
                  canManageUsers: true,
                  currentUserRole: 'Tenant Admin',
                  capabilities: {
                    canViewWorkspace: true,
                    canManageWorkspace: true,
                    canManageWorkOrders: true,
                    canManageDispatch: true,
                    canViewReports: true,
                    canViewAudit: true,
                    canManageUsers: true,
                    canManageInvites: true,
                  },
                });
                setWorkspace(current => ({ ...current, tenant: data.tenant }));
                setActiveSection('dashboard');
              }}
            />
          )}

          <section className="commercial-panel commercial-panel--subtle">
            <p className="commercial-panel__eyebrow">Overview</p>
            <h2 className="commercial-panel__title">Company Setup Status</h2>
            <p className="commercial-panel__copy">
              Company setup, user access, locations, customers, assets, technicians, work orders, dispatch, and reporting are ready to use here.
            </p>
          </section>
        </div>
      );
    }

    if (!hasWorkspace) return null;
    if (activeSection === 'dashboard') return <CommercialDashboardTab workspace={workspace} />;
    if (activeSection === 'schedule') return <CommercialScheduleTab api={api} user={user} workspace={workspace} capabilities={tenantContext.capabilities} onRefresh={loadWorkspace} />;
    if (activeSection === 'technicians') return <CommercialTechniciansTab api={api} user={user} workspace={workspace} capabilities={tenantContext.capabilities} onRefresh={loadWorkspace} />;
    if (activeSection === 'customers') return <CommercialCustomersTab api={api} user={user} workspace={workspace} capabilities={tenantContext.capabilities} onRefresh={loadWorkspace} />;
    if (activeSection === 'assets') return <CommercialAssetsTab api={api} user={user} workspace={workspace} capabilities={tenantContext.capabilities} onRefresh={loadWorkspace} />;
    if (activeSection === 'work-orders') return <CommercialWorkOrdersTab api={api} user={user} workspace={workspace} capabilities={tenantContext.capabilities} onRefresh={loadWorkspace} />;
    if (activeSection === 'dispatch') return <CommercialDispatchTab api={api} user={user} workspace={workspace} capabilities={tenantContext.capabilities} onRefresh={loadWorkspace} />;
    if (activeSection === 'coverage') return <CommercialWorkforceTab api={api} user={user} workspace={workspace} capabilities={tenantContext.capabilities} />;
    if (activeSection === 'reports') return <CommercialReportsTab api={api} user={user} workspace={workspace} />;
    if (activeSection === 'admin') return <CommercialAdminTab api={api} user={user} workspace={workspace} tenantContext={tenantContext} onRefresh={loadWorkspace} />;
    return null;
  };

  return (
    <div className="commercial-shell">
      <aside className="commercial-sidebar">
        <div className="commercial-brand">
          <p className="commercial-brand__eyebrow">Commercial</p>
          <h2 className="commercial-brand__title">FAST</h2>
          <p className="commercial-brand__subtitle">
            Repair management, scheduling, dispatch, and operations for commercial teams.
          </p>
        </div>

        <div className="commercial-sidebar__section">
          <p className="commercial-sidebar__label">Navigation</p>
          {visibleNavigationItems.map(item => (
            <button
              key={item.key}
              type="button"
              className={`commercial-nav-button ${activeSection === item.key ? 'is-active' : ''}`}
              onClick={() => setActiveSection(item.key)}
            >
              <span className="commercial-nav-button__icon">{item.icon}</span>
              <span className="commercial-nav-button__copy">
                <span className="commercial-nav-button__title">{item.title}</span>
                <span className="commercial-nav-button__description">{item.description}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="commercial-sidebar__status">
          <p className="commercial-sidebar__status-label">Quick View</p>
          <h3 className="commercial-sidebar__status-title">Operations Hub</h3>
          <p className="commercial-sidebar__status-copy">
            Manage customers, assets, technicians, work orders, dispatch, and admin setup from one place.
          </p>
        </div>
      </aside>

      <div className="commercial-main">
        <header className="commercial-topbar">
          <div className="commercial-topbar__copy">
            <h1>{activeNav.title}</h1>
            <p>{activeNav.description}</p>
          </div>
          <div className="commercial-topbar__badges">
            {user?.email ? (
              <span className="commercial-badge">
                {user.email}
              </span>
            ) : null}
            {tenantContext.currentUserRole ? (
              <span className={`commercial-badge ${tenantContext.currentUserRole === 'Tenant Admin' ? 'commercial-badge--admin' : ''}`}>
                {tenantContext.currentUserRole === 'Tenant Admin' ? 'Company Admin' : tenantContext.currentUserRole}
              </span>
            ) : null}
            <button
              type="button"
              className="commercial-topbar__logout"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </header>

        <div className="commercial-content">
          {workspaceError ? <div className="commercial-error">{workspaceError}</div> : null}
          {workspaceLoading ? <div className="commercial-panel"><p className="commercial-panel__copy">Loading your workspace...</p></div> : null}
          {renderActiveSection()}
        </div>
      </div>
    </div>
  );
}
